import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BeforeAfter } from "@/components/BeforeAfter";
import { GroundingBadge, SiteFooter } from "@/components/Chrome";
import { api, assetUrl } from "@/lib/api";
import { ProjectStatus } from "@/components/ProjectStatus";

const ExploreCard = ({ v }) => (
  <article data-testid={`explore-card-${v.id}`} className="group">
    <BeforeAfter before={assetUrl(v.beforeUrl)} after={assetUrl(v.afterUrl)} width={v.width} height={v.height} testid={`explore-slider-${v.id}`} />
    <div className="mt-3 flex items-start justify-between gap-3">
      <div>
        <div className="font-display text-xl leading-tight">{v.directionName || v.roomName || "Proposed transformation"}</div>
        {v.propertyTitle && <div className="mt-1 text-sm text-[var(--ink-soft)]" data-testid={`explore-card-address-${v.id}`}>{v.propertyTitle.replace(/,\s*[A-Z]{2}\s+\d{5}$/, "")}</div>}
        <div className="mt-1 text-xs text-[var(--stone-dark)]">{[v.roomName, v.style].filter(Boolean).join(" · ")}</div>
      </div>
      <GroundingBadge status={v.groundingStatus} testid={`explore-card-badge-${v.id}`} />
    </div>
    {v.project && <ProjectStatus project={v.project} compact testid={`explore-card-project-${v.id}`} />}
    <Link to={`/v/${v.id}`} className="btn-quiet mt-1" data-testid={`explore-card-open-${v.id}`}>
      View vision{v.hasWalkthrough ? " · walkthrough" : ""} →
    </Link>
  </article>
);

export default function Explore() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get("/explore").then((r) => setItems(r.data)).catch(() => setItems([])), []);
  useEffect(() => { load(); }, [load]);

  return (
    <main data-testid="explore-page">
      <div className="mx-auto max-w-7xl px-5 pt-12 sm:px-8 lg:px-12">
        <div className="eyebrow mb-3">Explore Possibilities</div>
        <h1 className="font-display text-4xl tracking-tight sm:text-5xl lg:text-6xl">Places brought back to life</h1>
        <p className="mt-4 max-w-xl text-sm text-[var(--ink-soft)] sm:text-base">Discover places worth improving. Explore proposed transformations, see what they could cost, and help make them happen.</p>
        {items === null && <div className="mt-16 h-64 rounded bg-[var(--bone-2)] shimmer" data-testid="explore-loading" />}
        {items?.length === 0 && (
          <div data-testid="explore-empty-state" className="mt-16 border-t border-[var(--charcoal)]/15 py-16">
            <p className="max-w-lg font-display text-3xl leading-snug">No visions yet. Be the first to reimagine a place.</p>
            <button className="btn-primary mt-8" data-testid="explore-empty-revitalize-button" onClick={() => nav("/#import")}>Start a Project</button>
          </div>
        )}
        {items?.length === 1 && (
          <div className="mt-12" data-testid="explore-featured-single">
            <div className="eyebrow mb-3" data-testid="explore-featured-label">Featured project</div>
            <div className="grid gap-10 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] lg:items-start">
              <ExploreCard v={items[0]} />
              <div className="border-t border-[var(--charcoal)]/15 pt-8 lg:mt-0" data-testid="explore-more-coming">
                <p className="max-w-sm font-display text-2xl leading-snug">More projects coming as residents start them.</p>
                <p className="mt-3 max-w-sm text-sm text-[var(--ink-soft)]">Every project on Explore passes our layout, plausibility and practicality checks before it appears here.</p>
                <button className="btn-primary mt-6" data-testid="explore-start-project-button" onClick={() => nav("/#import")}>Start a Project</button>
              </div>
            </div>
          </div>
        )}
        {items?.length > 1 && (
          <div className="mt-12 grid gap-x-8 gap-y-14 md:grid-cols-2" data-testid="explore-grid">
            {items.map((v) => <ExploreCard key={v.id} v={v} />)}
          </div>
        )}
      </div>
      <SiteFooter />
    </main>
  );
}
